import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CommonModule } from "@angular/common";
import { NavigationEnd, Router } from "@angular/router";

import { filter } from "rxjs";
import {
  AppSharedUserBottomSheetComponent,
  featureAvailability,
  withOfflineMenuAvailability,
  LektoAuthService,
  NotificationsStateService,
  OfflineModeStateService,
  ProfileConstants
} from "@frontends-v3/shared-core";
import { LktIconComponent, LktIconName, NavbarMenuItemType } from "@frontends-v3/design-system";

import { environment } from "../../../../environments/environment";
import { buildPlatformNavItems } from "../nav-items";
import { SECTION_MATCH_ROUTES } from "../nav-sections";

@Component({
  selector: "app-bottom-nav",
  standalone: true,
  imports: [CommonModule, LktIconComponent, AppSharedUserBottomSheetComponent],
  templateUrl: "./bottom-nav.component.html",
  styleUrl: "./bottom-nav.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BottomNavComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(LektoAuthService);
  public readonly notificationsState = inject(NotificationsStateService);
  private readonly offlineState = inject(OfflineModeStateService);

  private readonly currentUrl = signal<string>(this.router.url);

  // DEV-966 (CA 05): com o Modo Offline ligado os demais destinos seguem visíveis, desabilitados.
  // DEV-1104: mesma fonte do menu lateral — o leitor só tem Atribuições e Biblioteca.
  public readonly menuItems = computed<NavbarMenuItemType[]>(() =>
    withOfflineMenuAvailability(
      buildPlatformNavItems(ProfileConstants.isReaderProfile(this.authService.authProfile())),
      this.offlineState.enabled()
    )
  );

  public readonly appVersion = signal<string>(environment.version.split(".").slice(0, 2).join("."));

  /**
   * Mesmo gate de `AppSharedMenuComponent.showNotifications` (DEV-872): papel, não app.
   * `available()` fica falso quando o backend disse que a feature não existe para a sessão.
   */
  public readonly showNotifications = computed<boolean>(
    () =>
      // DEV-966 (CA 09): notificações pausadas com o Modo Offline ligado.
      featureAvailability("notifications", this.offlineState.enabled()) === "allowed" &&
      this.notificationsState.available() &&
      ProfileConstants.NOTIFICATION_PROFILES.includes(this.authService.authProfile())
  );

  public readonly normalizedUrl = computed<string>(() =>
    this.currentUrl().split("?")[0].toLowerCase()
  );

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.currentUrl.set(this.router.url));
  }

  public iconName(icon: string | undefined): LktIconName {
    return (icon ?? "") as LktIconName;
  }

  public isActive(item: NavbarMenuItemType): boolean {
    const url = this.normalizedUrl();
    const route = item.route?.toLowerCase();
    if (!route) return false;
    if (route === "/") return url === "/" || url === "";
    if (this.matchesPrefix(url, route)) return true;

    return (SECTION_MATCH_ROUTES[route] ?? []).some((extra) => this.matchesPrefix(url, extra));
  }

  private matchesPrefix(url: string, route: string): boolean {
    return url === route || url.startsWith(`${route}/`);
  }

  public onItemClick(item: NavbarMenuItemType): void {
    if (item.disabled || !item.route) return;
    this.router.navigate([item.route]);
  }

  public onNotificationsClick(event: MouseEvent): void {
    const bell = event.currentTarget as HTMLElement | null;
    if (bell) {
      this.notificationsState.togglePanel(bell);
    }
  }
}
